/**
 * Public pay-page data layer — reconciled to Mack's ACTUAL `getPaySession`
 * contract (functions/avbp_pay_session.js): a PUBLIC, CORS-enabled onRequest
 * HTTP function in region asia-south1, called with `?token=`. NOT an onCall
 * callable. Plus PURE helpers (UPI app deep-links, amount formatting,
 * surcharge/label logic, terminal-state mapping) that stay dependency-free +
 * unit-testable (no Firebase import at all — a plain `fetch`).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  RESPONSE ENVELOPE (top-level `state`, NOT session.status):
 *   { ok:true, state:'payable'|'already_paid'|'expired'|'not_payable',
 *     session?, quoteRef? }
 *   ok:false / non-200 → { ok:false, error } → a calm terminal screen.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ Endpoint ════════════════════ */

/**
 * The deployed public URL of Mack's onRequest fn (region asia-south1, project
 * aayatan-veda-hr). Firebase exposes the cloudfunctions.net alias for the v2
 * function; CORS is enabled on the function so the public page fetches it
 * cross-origin.
 */
export const PAY_ENDPOINT =
  'https://asia-south1-aayatan-veda-hr.cloudfunctions.net/getPaySession';

/* ════════════════════ Types (mirror the CF exactly) ════════════════════ */

export type PayMethodType = 'upi' | 'razorpay' | 'paypal' | 'bank' | 'stripe';

/** Fields every method card carries. `surchargeNote` is non-null ONLY on the
 *  processor rails (Razorpay gateway fee / PayPal surcharge); upi/bank = null. */
export interface PayMethodBase {
  type: PayMethodType;
  baseAmount: number;
  payAmount: number;
  payAmountDisplay: string;
  surchargeNote: string | null;
  /** Set by the CF when a processor link could not be minted this request. */
  unavailable?: boolean;
}

export interface UpiMethod extends PayMethodBase {
  type: 'upi';
  upiId: string;
  /** Prebuilt `upi://pay?…` — drives the QR AND the app deep-links. */
  qrPayload: string;
}
export interface RazorpayMethod extends PayMethodBase {
  type: 'razorpay';
  payUrl?: string;
}
export interface PaypalMethod extends PayMethodBase {
  type: 'paypal';
  approveUrl?: string;
  orderId?: string;
}
export interface BankMethod extends PayMethodBase {
  type: 'bank';
  fields: {
    acName?: string | null;
    bank?: string | null;
    acNo?: string | null;
    ifsc?: string | null;
    swift?: string | null;
    adCode?: string | null;
  };
}
export interface StripeMethod extends PayMethodBase {
  type: 'stripe';
  comingSoon?: boolean;
}

export type PayMethod =
  | UpiMethod
  | RazorpayMethod
  | PaypalMethod
  | BankMethod
  | StripeMethod;

export interface PaySession {
  token: string;
  quoteRef: string;
  quoteId: string;
  clientName: string;
  firmId: string;
  firmName: string;
  gstin: string | null;
  currency: string;
  amountDisplay: string;
  gstNote: string;
  status: 'payable';
  methods: PayMethod[];
  expiresAt: string | null;
}

/** Top-level state on the CF envelope. */
export type PayState = 'payable' | 'already_paid' | 'expired' | 'not_payable';

export interface PayResponse {
  ok: boolean;
  state?: PayState;
  session?: PaySession;
  quoteRef?: string;
  error?: string;
}

/** Terminal (non-payable) outcomes shown as a calm branded screen. */
export type PayTerminal = 'already_paid' | 'expired' | 'not_payable' | 'notfound' | 'error';

export type PayFetch =
  | { kind: 'ok'; session: PaySession }
  | { kind: 'terminal'; status: PayTerminal };

/* ════════════════════ PURE helpers (unit-tested) ════════════════════ */

/** PURE — a UPI amount string ("53000.00"), never negative, 2-dp per the spec. */
export function formatUpiAmount(amount: number): string {
  const n = Number(amount);
  return (Number.isFinite(n) && n > 0 ? n : 0).toFixed(2);
}

/**
 * PURE — the generic UPI intent (`upi://pay?…`). The CF already sends a
 * `qrPayload`, so the page uses THAT; this stays as a documented, tested mirror
 * of the same format (and a fallback if a payload were ever missing).
 */
export function buildUpiIntent(p: {
  upiId: string;
  firmName: string;
  amount: number;
  note: string;
}): string {
  const params = new URLSearchParams({
    pa: p.upiId,
    pn: p.firmName,
    am: formatUpiAmount(p.amount),
    cu: 'INR',
    tn: p.note,
  });
  return `upi://pay?${params.toString()}`;
}

export interface UpiAppLink {
  key: 'gpay' | 'phonepe' | 'paytm';
  label: string;
  /** app-specific scheme. */
  href: string;
}

/**
 * PURE — the app deep-links from a UPI payload (the CF's `qrPayload`).
 * Google Pay (tez://), PhonePe (phonepe://) and Paytm (paytmmp://) use their
 * OWN schemes and work reliably.
 *
 * BHIM is intentionally OMITTED (owner decision): its deep-link is too finicky
 * (custom `bhim://` is rejected; generic `upi://` is hijacked by WhatsApp Pay;
 * the Android intent URL is Android-Chrome only), and it is redundant with the
 * QR + copyable UPI id already on the card — both of which work in BHIM and
 * every other UPI app. The QR stays the generic `upi://` intent (scannable by
 * any app); on desktop these three schemes don't resolve and the QR/copy is the
 * universal fallback.
 */
export function upiAppLinks(qrPayload: string): UpiAppLink[] {
  const i = qrPayload.indexOf('?');
  const q = i >= 0 ? qrPayload.slice(i) : '';
  return [
    { key: 'gpay', label: 'Google Pay', href: `tez://upi/pay${q}` },
    { key: 'phonepe', label: 'PhonePe', href: `phonepe://pay${q}` },
    { key: 'paytm', label: 'Paytm', href: `paytmmp://pay${q}` },
  ];
}

/** PURE — the frontend card title for a method (the CF sends no label). */
export function methodLabel(m: PayMethod, currency: string): string {
  const usd = String(currency).toUpperCase() === 'USD';
  switch (m.type) {
    case 'upi':
      return 'UPI';
    case 'razorpay':
      return usd ? 'Cards — via Razorpay (International)' : 'Cards · Net Banking · Wallets';
    case 'paypal':
      return 'PayPal';
    case 'bank':
      return usd ? 'International bank transfer (wire)' : 'Bank transfer (NEFT / IMPS)';
    case 'stripe':
      return 'Stripe';
  }
}

/** PURE — the frontend helper line for a method. */
export function methodHelper(m: PayMethod, currency: string): string {
  const usd = String(currency).toUpperCase() === 'USD';
  switch (m.type) {
    case 'upi':
      return `Scan the QR or pay to ${(m as UpiMethod).upiId} · instant`;
    case 'razorpay':
      return usd ? 'Pay by international card · secure checkout' : 'via Razorpay — secure checkout';
    case 'paypal':
      return 'Pay with PayPal balance or any card';
    case 'bank':
      return usd ? 'SWIFT transfer from your bank' : 'Pay from your bank app';
    case 'stripe':
      return 'Card checkout for global clients';
  }
}

/** PURE — should this card render a muted surcharge sub-line? */
export function methodShowsSurcharge(m: PayMethod): boolean {
  return typeof m.surchargeNote === 'string' && m.surchargeNote.trim().length > 0;
}

/** PURE — map a top-level CF state to the page's terminal key. */
export function terminalFromState(state: PayState | string): PayTerminal {
  switch (state) {
    case 'already_paid':
      return 'already_paid';
    case 'expired':
      return 'expired';
    case 'not_payable':
      return 'not_payable';
    default:
      return 'error';
  }
}

/** PURE — map a non-200 HTTP status to a calm terminal key. */
export function terminalFromHttpStatus(status: number): PayTerminal {
  if (status === 404) return 'notfound';
  if (status === 400) return 'notfound'; // invalid/malformed token → treat as not found
  return 'error';
}

/* ════════════════════ Payment-confirmed landing (redirect target) ════════════════════
 *
 *  Razorpay payment links + PayPal redirect the payer back to
 *  `/payment-confirmed` after checkout. This page is a PURELY FRIENDLY landing —
 *  the webhook reconciles the payment server-side; the page NEVER verifies a
 *  payment or calls an API. It only reads the redirect query params to tailor
 *  the message, and degrades to a calm generic thank-you when params are
 *  missing (never an error / Not-Found).
 */

export type ConfirmState = 'confirmed' | 'pending' | 'cancelled' | 'generic';

export interface ConfirmResult {
  state: ConfirmState;
  /** A small/muted reference to show (payment id / order token), if any. */
  paymentRef: string | null;
}

/**
 * PURE — resolve the confirmation message from the redirect query params.
 * `q` is a plain getter map (works with URLSearchParams-derived records).
 *   • Razorpay: `razorpay_payment_link_status === 'paid'` → confirmed (ref =
 *     payment id); any other Razorpay status → pending.
 *   • PayPal (Mack's explicit flag): `paypal=success` → confirmed ·
 *     `paypal=pending` → pending · `paypal=cancelled` → cancelled (a calm
 *     "didn't complete", NOT an error). A bare PayPal return carrying `PayerID`
 *     with NO explicit flag → confirmed (payer approved; webhook reconciles).
 *   • Nothing recognizable → generic thank-you.
 */
export function resolvePaymentConfirmation(
  q: Record<string, string | null | undefined>,
): ConfirmResult {
  const rzpStatus = String(q.razorpay_payment_link_status || '').trim().toLowerCase();
  if (rzpStatus) {
    if (rzpStatus === 'paid') {
      return {
        state: 'confirmed',
        paymentRef: q.razorpay_payment_id || q.razorpay_payment_link_reference_id || null,
      };
    }
    return { state: 'pending', paymentRef: q.razorpay_payment_id || null };
  }
  const paypal = String(q.paypal || '').trim().toLowerCase();
  if (paypal) {
    const ref = q.token || q.PayerID || null;
    if (paypal === 'success') return { state: 'confirmed', paymentRef: ref };
    if (paypal === 'cancelled') return { state: 'cancelled', paymentRef: null };
    // 'pending' (and any other explicit-but-unknown paypal value) → confirming.
    return { state: 'pending', paymentRef: ref };
  }
  if (q.PayerID || q.paypalReturn) {
    return { state: 'confirmed', paymentRef: q.token || q.PayerID || null };
  }
  return { state: 'generic', paymentRef: null };
}

/* ════════════════════ Fetch (impure — plain HTTP, CORS) ════════════════════ */

/**
 * GET the pay-session from the public onRequest endpoint with `?token=`. Any
 * network error, non-200, `ok:false`, or non-'payable' state resolves to a
 * terminal key — the UI never shows a stack trace or a broken screen.
 */
export async function fetchPaySession(token: string): Promise<PayFetch> {
  let res: Response;
  try {
    res = await fetch(`${PAY_ENDPOINT}?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'terminal', status: 'error' }; // network / CORS failure
  }
  if (!res.ok) return { kind: 'terminal', status: terminalFromHttpStatus(res.status) };

  let data: PayResponse;
  try {
    data = (await res.json()) as PayResponse;
  } catch {
    return { kind: 'terminal', status: 'error' };
  }
  if (!data || data.ok !== true || !data.state) return { kind: 'terminal', status: 'error' };
  if (data.state === 'payable' && data.session) return { kind: 'ok', session: data.session };
  return { kind: 'terminal', status: terminalFromState(data.state) };
}
